/**
 * Login — supports two methods + MFA challenge:
 *   1. Email + password (default)         supabase.auth.signInWithPassword
 *   2. Magic link                          supabase.auth.signInWithOtp
 *
 * If the user has TOTP MFA enrolled, Supabase returns an aal1 session
 * after password login. We detect that, challenge the lowest-priority
 * verified TOTP factor, and ask for the 6-digit code before redirecting
 * to /dashboard.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { friendlyError } from '@/lib/utils/friendly-errors';
import { useAuth } from '@/lib/auth';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { CookieBanner } from '@/components/layout/CookieBanner';
import { PasswordInput } from '@/components/ui/PasswordInput';

type Mode = 'password' | 'magic';

/* ── SSO icon SVGs ─────────────────────────────────────────────── */

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.01 24.01 0 0 0 0 21.56l7.98-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const SSOSpinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

export function Login() {
  useEffect(() => { document.title = 'Sign in — Waypoint'; }, []);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Post-auth destination (set by ProtectedRoute on deep links, e.g. a ?trial=1
  // checkout link). Thread it through every auth path so it's never dropped.
  const nextParam = searchParams.get('next');
  const nextTarget = nextParam ?? '/dashboard';

  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ssoLoading, setSsoLoading] = useState<'google' | 'microsoft' | null>(null);

  // MFA challenge state — populated after password login if needed.
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  // True while we check for TOTP factors after password login. Without this
  // guard, onAuthStateChange sets `user` before listFactors() resolves and
  // the redirect effect bounces to /dashboard, skipping the MFA prompt.
  const [mfaCheckPending, setMfaCheckPending] = useState(false);

  // Already signed in (and not in MFA flow) → bounce
  useEffect(() => {
    if (!loading && user && !mfaChallengeId && !mfaCheckPending) {
      const next = searchParams.get('next');
      navigate(next ? decodeURIComponent(next) : '/dashboard', { replace: true });
    }
  }, [user, loading, navigate, searchParams, mfaChallengeId, mfaCheckPending]);

  async function handlePasswordLogin(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    setMfaCheckPending(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (signInError) throw signInError;

      // After password login, check if MFA is required.
      // aal2 needed = there's a verified TOTP factor and current session is only aal1.
      const { data: factorsData, error: factorsErr } = await supabase.auth.mfa.listFactors();
      if (factorsErr) throw factorsErr;
      const totp = factorsData.totp.find(f => f.status === 'verified');
      if (totp) {
        // Challenge it — user must enter 6-digit code to fully sign in
        const { data: challenge, error: challErr } = await supabase.auth.mfa.challenge({
          factorId: totp.id,
        });
        if (challErr) throw challErr;
        setMfaFactorId(totp.id);
        setMfaChallengeId(challenge.id);
        setSubmitting(false);
        return; // stop here; render MFA prompt
      }

      // No MFA → redirect handled by the useEffect once `user` updates
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
      setMfaCheckPending(false);
    }
  }

  async function handleMfaSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (!mfaFactorId || !mfaChallengeId) throw new Error('MFA state lost — please sign in again');
      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId: mfaFactorId,
        challengeId: mfaChallengeId,
        code: mfaCode.trim(),
      });
      if (verifyErr) throw verifyErr;
      // Clear MFA state; useEffect will redirect once user/session updates
      setMfaChallengeId(null);
      setMfaFactorId(null);
      setMfaCode('');
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMagicLink(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const next = searchParams.get('next') ?? '/dashboard';
      const { error: signInError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`,
        },
      });
      if (signInError) throw signInError;
      setMagicSent(true);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const handleGoogleSSO = async () => {
    setSsoLoading('google');
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextTarget)}` },
    });
    if (error) { setError('Google sign-in failed. Please try again.'); setSsoLoading(null); }
  };

  const handleMicrosoftSSO = async () => {
    setSsoLoading('microsoft');
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      // Entra only returns the user's address when the `email` scope is requested.
      // Without it Supabase fails the callback with "Error getting user email from
      // external provider" (seen in prod auth logs for a Microsoft 365 prospect).
      // Likewise the ID token's `name` claim (stored by Supabase as
      // user_metadata.full_name) needs `profile` — without it Microsoft users had no name.
      options: {
        scopes: 'email profile',
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(nextTarget)}`,
      },
    });
    if (error) { setError('Microsoft sign-in failed. Please try again.'); setSsoLoading(null); }
  };

  return (
    <AuthLayout>
      <div className="rounded-xl border border-border bg-surface-1 p-8 shadow-glow">
        {mfaChallengeId ? (
          // ----- MFA challenge -----
          <form onSubmit={handleMfaSubmit} className="space-y-4">
            <h1 className="mb-1 text-xl font-medium text-ink">Two-factor code</h1>
            <p className="mb-6 text-sm text-ink-muted">
              Enter the 6-digit code from your authenticator app.
            </p>
            <div>
              <label htmlFor="mfa" className="mb-1.5 block text-xs font-medium text-ink-dim">
                Verification code
              </label>
              <input
                id="mfa"
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                required
                autoFocus
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-center text-2xl font-mono tracking-[0.4em] text-ink outline-hidden focus:border-primary-line"
              />
            </div>
            {error && (
              <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting || mfaCode.length < 6}
              className="btn btn-primary w-full py-2.5"
            >
              {submitting ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={async () => { await supabase.auth.signOut(); setMfaChallengeId(null); setMfaFactorId(null); setMfaCode(''); }}
              className="block w-full text-center text-xs text-ink-dim hover:text-ink"
            >
              Cancel and use a different account
            </button>
          </form>
        ) : magicSent ? (
          // ----- Magic-link sent -----
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-2xl">&#10003;</div>
            <h1 className="mb-2 text-xl font-medium text-ink">Check your email</h1>
            <p className="text-sm text-ink-muted">
              We sent a sign-in link to <span className="font-mono text-primary-bright">{email}</span>. The link expires in 1 hour.
            </p>
            <button onClick={() => { setMagicSent(false); setEmail(''); }} className="btn mt-6">
              Use a different email
            </button>
          </div>
        ) : (
          // ----- Default: password (with magic-link toggle) -----
          <>
            <h1 className="mb-5 text-xl font-medium text-ink">Sign in</h1>

            {/* SSO buttons */}
            <div className="space-y-3 mb-2">
              <button
                type="button"
                onClick={handleGoogleSSO}
                disabled={ssoLoading !== null}
                className="w-full flex items-center justify-center gap-3 rounded-xl border border-border bg-surface-1 px-4 py-3 text-sm font-medium text-ink hover:bg-surface-2 transition-colors disabled:opacity-60"
              >
                {ssoLoading === 'google' ? <SSOSpinner /> : <GoogleIcon />}
                <span>Continue with Google</span>
              </button>
              <button
                type="button"
                onClick={handleMicrosoftSSO}
                disabled={ssoLoading !== null}
                className="w-full flex items-center justify-center gap-3 rounded-xl border border-border bg-surface-1 px-4 py-3 text-sm font-medium text-ink hover:bg-surface-2 transition-colors disabled:opacity-60"
              >
                {ssoLoading === 'microsoft' ? <SSOSpinner /> : <MicrosoftIcon />}
                <span>Continue with Microsoft</span>
              </button>
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-surface-1 px-3 text-ink-muted">or continue with email</span></div>
            </div>

            {/* Mode toggle */}
            <div className="mb-5 flex rounded-md border border-border bg-surface-2 p-0.5 text-2xs font-medium">
              <button
                type="button"
                onClick={() => { setMode('password'); setError(null); }}
                className={`flex-1 rounded-sm px-3 py-1.5 transition-colors ${mode === 'password' ? 'bg-primary text-white' : 'text-ink-dim hover:text-ink'}`}
              >
                Password
              </button>
              <button
                type="button"
                onClick={() => { setMode('magic'); setError(null); }}
                className={`flex-1 rounded-sm px-3 py-1.5 transition-colors ${mode === 'magic' ? 'bg-primary text-white' : 'text-ink-dim hover:text-ink'}`}
              >
                Magic link
              </button>
            </div>

            <form onSubmit={mode === 'password' ? handlePasswordLogin : handleMagicLink} className="space-y-4">
              <div>
                <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-ink-dim">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-ink outline-hidden focus:border-primary-line"
                />
              </div>

              {mode === 'password' && (
                <div>
                  <div className="mb-1.5 flex items-baseline justify-between">
                    <label htmlFor="password" className="text-xs font-medium text-ink-dim">
                      Password
                    </label>
                    <Link to="/forgot-password" className="text-2xs text-primary-bright hover:underline">
                      Forgot?
                    </Link>
                  </div>
                  <PasswordInput
                    id="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              )}

              {error && (
                <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !email || (mode === 'password' && !password)}
                className="btn btn-primary w-full py-2.5"
              >
                {submitting
                  ? mode === 'password' ? 'Signing in…' : 'Sending…'
                  : mode === 'password' ? 'Sign in' : 'Send magic link'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-ink-muted">
              New here? <Link to={nextParam ? `/signup?next=${encodeURIComponent(nextParam)}` : '/signup'} className="text-primary-bright hover:underline font-medium">Sign up</Link>
            </p>

            <p className="mt-4 text-center text-xs text-ink-dim">
              By signing in you agree to our{' '}
              <a href="/terms" className="text-primary-bright hover:underline">Terms</a> and{' '}
              <a href="/privacy" className="text-primary-bright hover:underline">Privacy Policy</a>.
            </p>
          </>
        )}
      </div>
      {/* The auth pages sit OUTSIDE MarketingLayout, so they were the one
          public surface with no consent banner — a visitor could land
          straight on /signup from an ad and never be asked. */}
      <CookieBanner />
    </AuthLayout>
  );
}
